// Экраны: функции рендера в строку HTML. Поведение — по prototype.html, действия — через data-act / data-go.
// Состояние и обработчики — в main.ts; здесь только разметка.

import { pickSource } from './audio.ts';
import { IC } from './icons.ts';
import {
  DICT_FILTERS, exampleReport, listenExamples, listenPaused, MAX_LISTEN, OWN, plural, dictTarget, esc, filterDict, genErrorText, highlight, isLearning, KIND_LABELS, LISTEN_LIMIT, mainExample, PROBLEM_LABELS,
  REASON_LABELS, reportOf, type PushView, senseOnFront, setDisableCounts, speakable, todaySummary, trackLabel, type AudioAvailability, type DictFilter, type ListenCard, type Overlay,
} from './logic.ts';
import type { Example, PoolOrder, Problem, Reason, Settings, Snapshot, Unit, UnitInput } from './types.ts';

export type Route =
  | { name: 'today' }
  | { name: 'dict' }
  | { name: 'add'; manual?: boolean }
  | { name: 'unit'; id: string }
  | { name: 'review'; id: string }
  | { name: 'settings' }
  | { name: 'progress' }
  | { name: 'session' };

export interface CardState {
  eventId: string;
  flipped: boolean;
  spoiler: boolean;
  plays: number;
}

export type Session =
  | { kind: 'intro'; unitIds: string[]; i: number; eventId: string; introduced: string[] }
  | { kind: 'text'; first: boolean; unitIds: string[]; i: number; card: CardState }
  | { kind: 'listen'; unitIds: string[]; i: number; card: ListenCard }
  | { kind: 'done'; first: boolean; count: number };

export interface FormState<T> {
  draft: T | null;
  errors: Record<string, string>;
  message: string;
  busy: boolean;
}

/** Фраза для генерации: форма и «где встретили». */
export interface DraftInput {
  form: string;
  sourceNote: string;
}

/** Наборы на «Добавлении»: подтверждение выключения (id набора), запрос в работе (id), сообщение. */
export interface SetsState {
  confirm: string;
  busy: string;
  message: string;
}

/** Экран проверки: правка, подтверждение (блоком в разметке, не window.confirm), ожидание ответа. */
export interface ReviewState {
  editing: boolean;
  confirm: '' | 'regen' | 'delete';
  busy: boolean;
  message: string;
}

/** Всё, что нужно экранам для рендера. */
export interface View {
  snap: Snapshot;
  overlay: Overlay;
  now: Date;
  online: boolean;
  canSpeak: boolean;
  /** Доступность записей для аудиоочереди (сеть или кэш устройства). */
  audio: AudioAvailability;
  pending: number;
  syncStatus: string;
  dict: { filter: DictFilter; q: string };
  session: Session | null;
  detail: { editing: boolean; fromSession: boolean };
  add: FormState<UnitInput>;
  draft: FormState<DraftInput>;
  edit: FormState<UnitInput>;
  review: ReviewState;
  sets: SetsState;
  settings: FormState<Settings>;
  push: PushUi;
}

/** Раздел «Уведомления»: состояние переключателя, запрос в работе, сообщение. */
export interface PushUi extends PushView {
  busy: boolean;
  testBusy: boolean;
  message: string;
}

// ——— общие куски ———

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];


const PHRASES: [string, string, string] = ['фраза', 'фразы', 'фраз'];
const CARDS: [string, string, string] = ['карточка', 'карточки', 'карточек'];

function problemText(p: Problem): string {
  return p ? PROBLEM_LABELS[p] : 'тип не указан';
}

/**
 * Карточка примера с прослушиванием; если на пример есть жалоба — предупреждение у него
 * и (вне занятия) переход к правке.
 */
function exampleCard(v: View, e: Example, label: string, canEdit: boolean, cls = 'card'): string {
  const r = exampleReport(e, v.overlay);
  const warn = r
    ? `<div class="warn">Отмечена ошибка: ${esc(problemText(r.problem).toLowerCase())}. Этот пример не звучит на аудиокарточке, пока его не исправят — новый текст закроет отметку.
        ${canEdit ? `<div style="margin-top:10px"><button class="secondary" data-act="edit" ${v.online ? '' : 'disabled'}>Исправить пример</button></div>` : ''}</div>`
    : '';
  return `<div class="${cls}"><div style="display:flex;justify-content:space-between;align-items:center"><span class="badge">${label}</span>${playBtn(v, e.audio, e.en, 'Прослушать пример')}</div>
      <div class="en">${highlight(e.en, e.target)}</div><div class="ru">${esc(e.ru)}</div>${warn}</div>`;
}

/** Аудиопримеры свёрнуты: их лучше не читать заранее, иначе аудиокарточка проверит память на текст. */
function listenBlock(v: View, u: Unit, canEdit: boolean): string {
  const ls = listenExamples(u);
  if (!ls.length) return '';
  const flagged = ls.filter((e) => exampleReport(e, v.overlay)).length;
  return `<details class="card listen-examples">
    <summary class="badge">Аудиопримеры (${ls.length}) — лучше не читать заранее${flagged ? ` · с ошибкой: ${flagged}` : ''}</summary>
    <div class="sub" style="font-size:13px">Они звучат только на аудиокарточке, по одному случайному за раз.</div>
    ${ls.map((e) => exampleCard(v, e, `Аудиопример ${e.slot}`, canEdit, 'subcard')).join('')}
  </details>`;
}

function nav(active: string): string {
  const b = (id: string, label: string, ic: string) =>
    `<button class="${active === id ? 'on' : ''}" data-go="${id}">${ic}${label}</button>`;
  return `<nav class="nav">${b('today', 'Учёба', IC.home)}${b('dict', 'Словарь', IC.book)}${b('progress', 'Прогресс', IC.chart)}</nav>`;
}

/** Кнопка прослушивания: сохранённая запись, без неё — голос ОС; без обоих — неактивна. */
function playBtn(v: View, key: string | null, text: string, label: string, cls = 'icon-btn', inner = IC.spk): string {
  const dis = pickSource({ key, fallbackText: text }, v.canSpeak) ? '' : ' disabled title="Озвучка недоступна в этом браузере"';
  return `<button class="${cls}" data-act="say" data-k="${esc(key ?? '')}" data-t="${esc(text)}" aria-label="${esc(label)}"${dis}>${inner}</button>`;
}

function syncLine(v: View): string {
  if (v.pending > 0) {
    const where = v.online && v.syncStatus !== 'offline' ? 'отправляются…' : 'отправятся, когда появится сеть';
    return `<div class="sub sync">Ответов на устройстве: ${v.pending} · ${where}</div>`;
  }
  if (!v.online || v.syncStatus === 'offline') return `<div class="sub sync">Нет сети · данные с последней синхронизации</div>`;
  return '';
}

function offlineNote(v: View, what: string): string {
  return v.online ? '' : `<div class="warn">Нет сети. ${what} можно только при подключении к серверу.</div>`;
}

function fieldErr(errors: Record<string, string>, key: string): string {
  return errors[key] ? `<div class="err" role="alert">${esc(errors[key])}</div>` : '';
}

function unitById(v: View, id: string): Unit | undefined {
  return v.snap.units.find((u) => u.id === id);
}

const STATUS_CHIPS: Record<string, [string, string]> = {
  generating: ['st-gen', 'Готовится…'],
  review: ['st-review', 'На проверке'],
  gen_failed: ['st-fail', 'Не удалось подготовить'],
};

function statusChip(u: Unit, o: Overlay): string {
  const learning = isLearning(u, o);
  const [cls, txt] = STATUS_CHIPS[u.status] ?? (learning ? ['st-learn', 'Изучается'] : ['st-pool', 'В пуле']);
  const flag = reportOf(u, o) ? '<span class="status st-flag">пример с ошибкой</span>' : '';
  return `<span class="chips-inline">${flag}<span class="status ${cls}">${txt}${u.isHard ? ' · трудная' : ''}</span></span>`;
}

// ——— вход и загрузка ———

export function loginView(error: string, busy: boolean, email: string): string {
  return `<div class="screen">
    <div class="center" style="flex:0 0 auto;padding-top:10vh">
      <h1>Вход</h1>
      <div class="sub">Тренажёр понимания английской речи</div>
    </div>
    <form class="card" data-form="login">
      <label class="field">Email<input type="email" name="email" autocomplete="username" required value="${esc(email)}"></label>
      <label class="field">Пароль<input type="password" name="password" autocomplete="current-password" required></label>
      ${error ? `<div class="err" role="alert">${esc(error)}</div>` : ''}
      <button class="primary" type="submit" ${busy ? 'disabled' : ''}>${busy ? 'Входим…' : 'Войти'}</button>
    </form>
  </div>`;
}

export function bootView(offline: boolean): string {
  return `<div class="screen"><div class="center">
    ${offline
      ? `<h1>Нет сети</h1><div class="sub" style="max-width:280px">Для первого входа нужно подключение к серверу. Потом приложение будет открываться и без сети.</div><button class="primary" style="max-width:280px" data-act="retry">Повторить</button>`
      : `<div class="sub">Загрузка…</div>`}
  </div></div>`;
}

// ——— Сегодня ———

export function todayView(v: View): string {
  const s = todaySummary(v.snap.units, v.overlay, v.snap.settings, v.now, v.audio);
  const d = v.now;
  const date = `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  const date1 = date[0].toUpperCase() + date.slice(1);
  const text = s.textDue > 0
    ? `<button class="row-btn" data-act="startText"><div class="ic">${IC.text}</div><div class="txt"><div class="main">Текст · ${s.textDue} ${plural(s.textDue, CARDS)}</div><div class="sub">можно где угодно</div></div>${IC.chev}</button>`
    : `<div class="row-btn" aria-disabled="true"><div class="ic">${IC.text}</div><div class="txt"><div class="main">Текст · 0</div><div class="sub">На сегодня всё повторено</div></div></div>`;
  const audio = s.listenDue > 0
    ? `<button class="row-btn" data-act="startListen"><div class="ic">${IC.audio}</div><div class="txt"><div class="main">Аудио · ${s.listenDue} ${plural(s.listenDue, CARDS)}</div><div class="sub">нужны наушники</div></div>${IC.chev}</button>`
    : `<div class="row-btn" aria-disabled="true"><div class="ic">${IC.audio}</div><div class="txt"><div class="main">Аудио · 0</div><div class="sub">На сегодня всё прослушано</div></div></div>`;
  const why =
    s.introBlocked === 'empty_pool' ? `<div class="sub">Пул пуст. Добавьте фразы в <a href="#/dict">словаре</a>, чтобы начать знакомство.</div>`
    : s.introBlocked === 'disabled' ? `<div class="sub">В <a href="#/settings">настройках</a> выбрано 0 новых фраз за занятие.</div>`
    : '';
  const overflow = s.introBlocked === 'audio_overflow'
    ? `<div class="warn">В аудио накопилось ${s.listenDue} ${plural(s.listenDue, CARDS)}. Новые фразы откроются, когда в очереди останется меньше ${LISTEN_LIMIT}.</div>`
    : '';
  return `<div class="screen">
    <div class="top"><div><div class="sub">${date1}</div><h1>Сегодня</h1></div>
      <button class="icon-btn" data-go="settings" aria-label="Настройки">${IC.gear}</button></div>
    ${syncLine(v)}
    <div class="label">Повторения</div>
    ${text}
    ${audio}
    <div class="label">Новое</div>
    <div class="card">
      <div style="display:flex;justify-content:space-between"><span>В пуле «к изучению»</span><b>${s.pool.total} ${plural(s.pool.total, PHRASES)}</b></div>
      <div class="sub">${s.pool.own} ${plural(s.pool.own, OWN)} · ${s.pool.fromSets} из наборов</div>
      ${overflow}
      <button class="primary" data-act="startIntro" ${s.introBlocked ? 'disabled' : ''}>Начать знакомство${s.introCount ? ` · ${s.introCount}` : ''}</button>
      ${why}
      <div class="sub" style="font-size:13px">Сколько новых за занятие и в каком порядке брать — в <a href="#/settings">настройках</a></div>
    </div>
  </div>${nav('today')}`;
}

// ——— Словарь ———

export function dictView(v: View): string {
  return `<div class="screen">
    <div class="top"><h1>Словарь</h1><button class="secondary" data-go="add" style="display:flex;align-items:center;gap:6px">${IC.plus}Добавить</button></div>
    ${syncLine(v)}
    <input type="search" id="search" placeholder="Поиск по фразе или значению" value="${esc(v.dict.q)}" aria-label="Поиск" autocomplete="off">
    <div class="chips">${DICT_FILTERS.map(([id, l]) => `<button class="chip ${v.dict.filter === id ? 'on' : ''}" data-act="filter" data-f="${id}">${l}</button>`).join('')}</div>
    <div class="card list" id="dictList">${dictList(v)}</div>
  </div>${nav('dict')}`;
}

export function dictList(v: View): string {
  const rows = filterDict(v.snap.units, v.overlay, v.dict.filter, v.dict.q);
  if (!rows.length) {
    return v.snap.units.length
      ? '<div class="sub" style="padding:14px 0">Ничего не найдено</div>'
      : '<div class="sub" style="padding:14px 0">Словарь пуст. Нажмите «Добавить», чтобы внести первую фразу.</div>';
  }
  return rows
    .map((u) => {
      const href = dictTarget(u);
      const meaning = u.meaning || (u.status === 'gen_failed' ? 'карточка не готова' : 'значение готовится');
      const inner = `<span class="item-txt"><span style="font-weight:600">${esc(u.form)}${u.senseLabel ? ` <span class="sub">${esc(u.senseLabel)}</span>` : ''}</span><span class="sub">${esc(meaning)}</span></span>
      ${statusChip(u, v.overlay)}`;
      // Пока фраза готовится, открывать нечего.
      return href ? `<a class="item" href="${href}">${inner}</a>` : `<div class="item" aria-disabled="true">${inner}</div>`;
    })
    .join('');
}

// ——— Форма единицы (добавление и правка) ———

const emptyEx = () => ({ en: '', ru: '', target: '' });

export function emptyUnitInput(): UnitInput {
  return { kind: 'expression', form: '', meaning: '', senseLabel: '', explanation: '', sourceNote: '',
    main: emptyEx(), listen: Array.from({ length: MAX_LISTEN }, emptyEx) };
}

export function unitToInput(u: Unit): UnitInput {
  const ex = (e: Example | undefined) => ({ en: e?.en ?? '', ru: e?.ru ?? '', target: e?.target ?? '' });
  const ls = listenExamples(u);
  return { kind: u.kind, form: u.form, meaning: u.meaning, senseLabel: u.senseLabel, explanation: u.explanation,
    sourceNote: u.sourceNote, main: ex(mainExample(u)),
    listen: Array.from({ length: MAX_LISTEN }, (_, i) => ex(ls.find((e) => e.slot === i + 1))) };
}

function unitForm(name: 'add' | 'edit', f: FormState<UnitInput>, online: boolean, submit: string): string {
  const d = f.draft ?? emptyUnitInput();
  const e = f.errors;
  const inp = (key: string, label: string, val: string, ph = '') =>
    `<label class="field">${label}<input type="text" name="${key}" value="${esc(val)}" placeholder="${esc(ph)}"${e[key] ? ' aria-invalid="true"' : ''}>${fieldErr(e, key)}</label>`;
  const area = (key: string, label: string, val: string, ph = '') =>
    `<label class="field">${label}<textarea name="${key}" placeholder="${esc(ph)}"${e[key] ? ' aria-invalid="true"' : ''}>${esc(val)}</textarea>${fieldErr(e, key)}</label>`;
  const kinds = Object.entries(KIND_LABELS)
    .map(([k, l]) => `<option value="${k}" ${d.kind === k ? 'selected' : ''}>${l}</option>`).join('');
  // Блок аудиопримера раскрыт, если он заполнен или в нём ошибка.
  const listen = Array.from({ length: MAX_LISTEN }, (_, i) => {
    const x = d.listen[i] ?? emptyEx();
    const p = `listen.${i + 1}`;
    const open = !!(x.en || x.ru || x.target || e[`${p}.en`] || e[`${p}.ru`] || e[`${p}.target`]);
    return `<details class="card" ${open ? 'open' : ''}>
      <summary class="badge">Аудиопример ${i + 1} (необязательно)</summary>
      ${area(`${p}.en`, 'Пример на английском', x.en)}
      ${area(`${p}.ru`, 'Перевод', x.ru)}
      ${inp(`${p}.target`, 'Целевой фрагмент (как в примере)', x.target)}
    </details>`;
  }).join('');
  return `<form class="form" data-form="${name}" novalidate>
    <div class="card">
      ${inp('form', 'Слово или фраза *', d.form, 'например, figure out')}
      <label class="field">Тип *<select name="kind">${kinds}</select>${fieldErr(e, 'kind')}</label>
      ${inp('senseLabel', 'Метка значения — только для многозначных, контекст, а не перевод', d.senseLabel, '(о неожиданных делах)')}
      ${inp('meaning', 'Значение *', d.meaning, 'разобраться, понять')}
      ${area('explanation', 'Объяснение *', d.explanation, 'когда и как так говорят')}
    </div>
    <div class="card">
      <span class="badge">Основной пример *</span>
      ${area('main.en', 'Пример на английском', d.main.en, "I can't figure out how this works.")}
      ${area('main.ru', 'Перевод', d.main.ru)}
      ${inp('main.target', 'Целевой фрагмент (как в примере)', d.main.target, 'figure out')}
    </div>
    <div class="sub" style="font-size:13px">Аудиопримеры звучат только на аудиокарточке: другие ситуации с той же фразой, лучше в разных формах. Без них аудиокарточка играет основной пример.</div>
    ${listen}
    <div class="card">${area('sourceNote', 'Где встретили (необязательно)', d.sourceNote, 'фраза из видео, контекст…')}</div>
    ${f.message ? `<div class="err" role="alert">${esc(f.message)}</div>` : ''}
    ${online ? '' : `<div class="warn">Нет сети. Сохранить можно только при подключении к серверу.</div>`}
    <button class="primary" type="submit" ${f.busy || !online ? 'disabled' : ''}>${f.busy ? 'Сохраняем…' : submit}</button>
  </form>`;
}

/** Быстрое добавление: фраза и «где встретили», остальное подготовит генерация. */
function draftForm(v: View): string {
  const f = v.draft;
  const d = f.draft ?? { form: '', sourceNote: '' };
  const e = f.errors;
  return `<form class="card" data-form="draft" novalidate>
      <label class="field">Слово или фраза<input type="text" name="form" value="${esc(d.form)}" placeholder="например, figure out"${e.form ? ' aria-invalid="true"' : ''}>${fieldErr(e, 'form')}</label>
      <label class="field">Где встретили (необязательно)<textarea name="sourceNote" placeholder="фраза из видео, контекст…"${e.sourceNote ? ' aria-invalid="true"' : ''}>${esc(d.sourceNote)}</textarea>${fieldErr(e, 'sourceNote')}</label>
      ${f.message ? `<div class="err" role="alert">${esc(f.message)}</div>` : ''}
      ${v.online ? '' : `<div class="warn">Нет сети. Добавить фразу можно только при подключении к серверу.</div>`}
      <button class="primary" type="submit" ${f.busy || !v.online ? 'disabled' : ''}>${f.busy ? 'Добавляем…' : 'Добавить'}</button>
      <div class="sub" style="font-size:13px">Значение, объяснение и примеры подготовит ИИ — вы проверите их перед изучением. <a href="#/add/manual">Заполнить вручную</a></div>
    </form>`;
}

/** Готовые наборы: «Добавить в пул» / «В пуле ✓»; выключение — после подтверждения с числами. */
function setsList(v: View): string {
  const sets = v.snap.sets ?? [];
  if (!sets.length) return '';
  const st = v.sets;
  const off = v.online ? '' : ' disabled';
  const rows = sets.map((x) => {
    const busy = st.busy === x.id ? ' disabled' : '';
    const row = `<div class="item"><div class="item-txt"><div style="font-weight:500">${esc(x.title)}</div>
        <div class="sub">${x.count} ${plural(x.count, PHRASES)}${x.description ? ` · ${esc(x.description)}` : ''}</div></div>
        <button class="chip ${x.enabled ? 'on' : ''}" data-act="${x.enabled ? 'setOffAsk' : 'setOn'}" data-id="${esc(x.id)}" aria-pressed="${x.enabled}"${off}${busy}>${x.enabled ? 'В пуле ✓' : 'Добавить в пул'}</button></div>`;
    if (st.confirm !== x.id) return row;
    const c = setDisableCounts(v.snap.units, v.overlay, x.id);
    return row + `<div class="warn" role="alertdialog">Убрать набор «${esc(x.title)}» из пула? Уйдёт ${c.leave} ${plural(c.leave, PHRASES)} без знакомства${c.stay ? `, останется ${c.stay} ${plural(c.stay, PHRASES)} — с ${plural(c.stay, ['ней', 'ними', 'ними'])} вы уже познакомились, прогресс сохранится` : ''}.
        <div style="display:flex;gap:8px;margin-top:10px">
          <button class="primary grow" data-act="setOff" data-id="${esc(x.id)}"${off}${busy}>Убрать</button>
          <button class="secondary grow" data-act="setCancel">Отмена</button></div></div>`;
  }).join('');
  return `<div class="label">Готовые наборы</div>
    <div class="card list">${rows}</div>
    ${st.message ? `<div class="err" role="alert">${esc(st.message)}</div>` : ''}
    ${v.online ? '' : '<div class="warn">Нет сети. Включать и выключать наборы можно только при подключении к серверу.</div>'}`;
}

/** Пояснение над ручной формой, если генерация выключилась, пока «Добавление» было открыто. */
export const GEN_OFF_NOTICE = 'Генерация на сервере сейчас выключена — заполните вручную';

export function addView(v: View, manual = false): string {
  const quick = !!v.snap.generation && !manual;
  const genOff = !quick && v.add.message === GEN_OFF_NOTICE;
  return `<div class="screen">
    <div class="top"><button class="icon-btn" data-go="${manual && v.snap.generation ? 'add' : 'dict'}" aria-label="Назад">${IC.back}</button><div class="title">Добавление</div><div style="width:44px"></div></div>
    ${genOff ? `<div class="warn" role="status">${GEN_OFF_NOTICE}</div>` : ''}
    ${quick ? draftForm(v) : unitForm('add', genOff ? { ...v.add, message: '' } : v.add, v.online, 'Добавить')}
    ${setsList(v)}
  </div>`;
}

// ——— Проверка перед изучением ———

export function reviewView(v: View, id: string): string {
  const u = unitById(v, id);
  const r = v.review;
  const top = (title: string) =>
    `<div class="top"><button class="icon-btn" data-go="dict" aria-label="Назад">${IC.back}</button><div class="title">${title}</div><div style="width:44px"></div></div>`;
  if (!u) return `<div class="screen">${top('Проверка')}<div class="sub">Фраза не найдена.</div></div>`;
  const off = v.online ? '' : ' disabled';
  const busy = r.busy ? ' disabled' : '';
  const msg = r.message ? `<div class="err" role="alert">${esc(r.message)}</div>` : '';
  const offline = offlineNote(v, 'Проверять, править и удалять фразы');
  const del = `<button class="secondary" data-act="reviewDelete"${off}${busy}>Удалить</button>`;
  const confirmBlock = r.confirm
    ? `<div class="warn" role="alertdialog">
        ${r.confirm === 'regen' ? 'Ваши правки этой фразы будут потеряны: перегенерация заменит все поля.' : `Удалить «${esc(u.form)}»? Фраза пропадёт из словаря.`}
        <div style="display:flex;gap:8px;margin-top:10px">
          <button class="primary grow" data-act="${r.confirm === 'regen' ? 'reviewRegenYes' : 'reviewDeleteYes'}"${off}${busy}>${r.confirm === 'regen' ? 'Перегенерировать' : 'Удалить'}</button>
          <button class="secondary grow" data-act="reviewCancel">Отмена</button></div></div>`
    : '';
  if (r.editing) {
    const manual = u.status === 'gen_failed';
    return `<div class="screen">
      <div class="top"><button class="icon-btn" data-act="cancelEdit" aria-label="Отменить">${IC.x}</button><div class="title">${manual ? 'Заполнить вручную' : 'Правка перед изучением'}</div><div style="width:44px"></div></div>
      ${unitForm('edit', v.edit, v.online, manual ? 'Сохранить → в пул' : 'Сохранить')}
      ${manual ? '' : '<div class="sub" style="font-size:13px">После сохранения фраза останется на проверке до одобрения.</div>'}
    </div>`;
  }
  if (u.status === 'generating') {
    return `<div class="screen">${top('Проверка')}
      <div><div class="phrase">${esc(u.form)}</div></div>
      <div class="card"><div class="sub" aria-live="polite">Карточка готовится… Обычно это занимает несколько секунд.</div></div>
      ${confirmBlock}${msg}${offline}${r.confirm ? '' : del}
    </div>`;
  }
  if (u.status === 'gen_failed') {
    return `<div class="screen">${top('Не удалось подготовить')}
      <div><div class="phrase">${esc(u.form)}</div></div>
      <div class="warn">${esc(genErrorText(u.genError))} Фраза сохранена: можно повторить генерацию, заполнить карточку вручную или удалить фразу.</div>
      ${u.sourceNote ? `<div class="card"><div class="label">Где встретили</div><div class="sub">${esc(u.sourceNote)}</div></div>` : ''}
      ${confirmBlock}${msg}${offline}
      ${r.confirm ? '' : `<button class="primary" data-act="reviewRegen"${off}${busy}>Повторить</button>
      <div style="display:flex;gap:8px"><button class="secondary grow" data-act="reviewEdit"${off}${busy}>Заполнить вручную</button>${del.replace('class="secondary"', 'class="secondary grow"')}</div>`}
    </div>`;
  }
  if (u.status !== 'review') {
    return `<div class="screen">${top('Проверка')}<div class="sub">Фраза уже одобрена.</div>
      <button class="secondary" data-go="unit/${encodeURIComponent(u.id)}">Открыть фразу</button></div>`;
  }
  const main = mainExample(u);
  return `<div class="screen">
    <div class="top"><button class="icon-btn" data-go="dict" aria-label="Назад">${IC.back}</button><div class="title">Проверка перед изучением</div>${playBtn(v, u.phraseAudio, speakable(u.form), 'Прослушать фразу')}</div>
    <div><div class="phrase">${esc(u.form)}</div>${u.senseLabel ? `<div class="sense">${esc(u.senseLabel)}</div>` : ''}
      <div class="sub" style="margin-top:6px">${KIND_LABELS[u.kind] ?? u.kind}${u.editedInReview ? ' · поправлено вами' : ''}</div></div>
    <div class="card"><div class="label">Значение</div><div class="meaning">${esc(u.meaning)}</div><div class="sub">${esc(u.explanation)}</div></div>
    ${main ? exampleCard(v, main, 'Основной пример', false) : ''}${listenBlock(v, u, false)}
    ${u.sourceNote ? `<div class="card"><div class="label">Где встретили</div><div class="sub">${esc(u.sourceNote)}</div></div>` : ''}
    ${confirmBlock}${msg}${offline}
    ${r.confirm ? '' : `<div style="display:flex;gap:8px"><button class="secondary grow" data-act="reviewEdit"${off}${busy}>Править</button><button class="secondary grow" data-act="reviewRegen"${off}${busy}>Перегенерировать</button></div>
    <button class="primary" data-act="approve"${off}${busy}>${r.busy ? 'Сохраняем…' : 'Одобрить → в пул'}</button>
    ${del}`}
  </div>`;
}

// ——— Подробный вид ———

export function detailView(v: View, id: string): string {
  const u = unitById(v, id);
  const fromSession = v.detail.fromSession;
  const backAct = fromSession ? 'data-act="backToSession"' : 'data-go="dict"';
  const backLabel = fromSession ? 'Вернуться к занятию' : 'Назад';
  const top = (title: string, right = '<div style="width:44px"></div>') =>
    `<div class="top"><button class="icon-btn" ${backAct} aria-label="${backLabel}">${IC.back}</button><div class="title">${title}</div>${right}</div>`;
  if (!u) {
    return `<div class="screen">${top('Словарь')}<div class="sub">Фраза не найдена.</div></div>`;
  }
  if (v.detail.editing) {
    return `<div class="screen">
      <div class="top"><button class="icon-btn" data-act="cancelEdit" aria-label="Отменить">${IC.x}</button><div class="title">Редактирование</div><div style="width:44px"></div></div>
      ${unitForm('edit', v.edit, v.online, 'Сохранить')}
      <div class="sub" style="font-size:13px">Правка не сбрасывает прогресс. Изменённый пример сохраняется новой версией, история ответов остаётся привязана к прежней.</div>
    </div>`;
  }
  const learning = isLearning(u, v.overlay);
  const textLbl = trackLabel(u.text, v.now, learning ? 'ждёт первой проверки' : 'ещё не изучается (в пуле)');
  // Общее предупреждение — только когда аудиокарточке нечего играть из-за жалоб; жалоба на один
  // из примеров показана у него самого.
  const paused = listenPaused(u, v.overlay);
  const listenLbl = paused ? 'отложено до исправления примера' : trackLabel(u.listen, v.now, 'ещё закрыто (сначала текст)');
  const reportWarn = paused
    ? `<div class="warn">Во всех примерах, которые может сыграть аудиокарточка, отмечена ошибка. Аудиокарточка отложена, пока их не исправят — новый текст примера закроет отметку.
        ${fromSession ? '' : `<div style="margin-top:10px"><button class="secondary" data-act="edit" ${v.online ? '' : 'disabled'}>Исправить пример</button></div>`}</div>`
    : '';
  const main = mainExample(u);
  return `<div class="screen">
    ${top(fromSession ? 'Во время занятия' : 'Словарь', playBtn(v, u.phraseAudio, speakable(u.form), 'Прослушать фразу'))}
    <div><div class="phrase">${esc(u.form)}</div>${u.senseLabel ? `<div class="sense">${esc(u.senseLabel)}</div>` : ''}
      <div class="sub" style="margin-top:6px">${KIND_LABELS[u.kind] ?? u.kind} · ${statusChip(u, v.overlay)}</div></div>
    ${reportWarn}
    <div class="card"><div class="label">Значение</div><div class="meaning">${esc(u.meaning)}</div><div class="sub">${esc(u.explanation)}</div></div>
    ${main ? exampleCard(v, main, 'Основной пример', !fromSession && !paused) : ''}${listenBlock(v, u, !fromSession && !paused)}
    ${u.sourceNote ? `<div class="card"><div class="label">Где встретили</div><div class="sub">${esc(u.sourceNote)}</div></div>` : ''}
    <div class="card"><div class="label">Прогресс</div>
      <div class="kv"><span>Текст</span><span class="sub">${esc(textLbl)}</span></div>
      <div class="kv"><span>Слух</span><span class="sub">${esc(listenLbl)}</span></div>
      ${u.isHard ? '<div class="warn">Трудная фраза</div>' : ''}
      <div class="sub" style="font-size:13px">Просмотр этой страницы не меняет расписание</div></div>
    ${fromSession
      ? `<button class="primary" data-act="backToSession">Вернуться к занятию</button>`
      : `<button class="secondary" data-act="edit" ${v.online ? '' : 'disabled'}>Редактировать</button>${offlineNote(v, 'Редактировать')}`}
  </div>`;
}

// ——— Занятие ———

function sessionTop(title: string, i: number, total: number): string {
  const pct = Math.round((i / total) * 100);
  return `<div class="top"><button class="icon-btn" data-act="quit" aria-label="Прервать занятие">${IC.x}</button><div class="title">${title} · ${i + 1} из ${total}</div><div style="width:44px"></div></div>
    <div class="progressbar"><div style="width:${pct}%"></div></div>`;
}

export function introView(v: View, s: Extract<Session, { kind: 'intro' }>): string {
  const u = unitById(v, s.unitIds[s.i]);
  if (!u) return `<div class="screen"><div class="sub">Фраза не найдена.</div><button class="primary" data-act="quit">На главный экран</button></div>`;
  const ex = mainExample(u);
  const last = s.i === s.unitIds.length - 1;
  return `<div class="screen">
    ${sessionTop('Знакомство', s.i, s.unitIds.length)}
    <div><div class="phrase">${esc(u.form)}</div>${u.senseLabel ? `<div class="sense">${esc(u.senseLabel)}</div>` : ''}</div>
    <div class="card"><div class="label">Значение</div><div class="meaning">${esc(u.meaning)}</div><div class="sub">${esc(u.explanation)}</div></div>
    ${ex ? `<div class="card"><div class="en">${highlight(ex.en, ex.target)}</div><div class="ru">${esc(ex.ru)}</div>
      <div style="display:flex;gap:8px">${playBtn(v, ex.audio, ex.en, 'Прослушать пример', 'secondary grow', 'Пример')}${playBtn(v, u.phraseAudio, speakable(u.form), 'Прослушать только фразу', 'secondary grow', 'Только фраза')}</div></div>` : ''}
    <div style="flex:1"></div>
    <button class="primary" data-act="introNext" data-i="${s.i}">${last ? 'К первой проверке →' : 'Дальше →'}</button>
  </div>`;
}

export function textCardView(v: View, s: Extract<Session, { kind: 'text' }>): string {
  const u = unitById(v, s.unitIds[s.i]);
  if (!u) return `<div class="screen"><div class="sub">Фраза не найдена.</div><button class="primary" data-act="skipCard" data-i="${s.i}">Дальше</button></div>`;
  const c = s.card;
  const ex = mainExample(u);
  const front = `<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">
      <div><div class="phrase">${esc(u.form)}</div>${senseOnFront(u, v.snap.units) ? `<div class="sense">${esc(u.senseLabel)}</div>` : ''}</div>
      ${playBtn(v, u.phraseAudio, speakable(u.form), 'Прослушать фразу')}</div>`;
  const sp = !ex ? ''
    : c.spoiler || c.flipped
      ? `<div class="spoiler open"><div class="en" style="font-size:17px">${highlight(ex.en, ex.target)}</div></div>`
      : `<button class="spoiler" data-act="spoiler">Нажмите, чтобы увидеть пример</button>`;
  const back = c.flipped
    ? `<div class="card">${ex ? `<div class="ru">${esc(ex.ru)}</div>` : ''}<div class="meaning">${esc(u.meaning)}</div>${u.senseLabel && !senseOnFront(u, v.snap.units) ? `<div class="sense">${esc(u.senseLabel)}</div>` : ''}<div class="sub">${esc(u.explanation)}</div>
        <button class="more" data-act="openDetail" data-id="${esc(u.id)}">Подробнее о фразе →</button></div>
      <div class="sub" style="text-align:center">Насколько легко вспомнили значение?</div>
      <div class="rates">
        <button class="rate r1" data-act="rate" data-r="again" data-i="${s.i}">Не понял</button>
        <button class="rate r2" data-act="rate" data-r="hard" data-i="${s.i}">С трудом</button>
        <button class="rate r3" data-act="rate" data-r="good" data-i="${s.i}">Легко</button></div>`
    : `<div style="flex:1"></div><button class="primary" data-act="flip">Показать ответ</button>`;
  return `<div class="screen">${sessionTop(s.first ? 'Первая проверка' : 'Текст', s.i, s.unitIds.length)}${front}${sp}${back}</div>`;
}

export function listenCardView(v: View, s: Extract<Session, { kind: 'listen' }>): string {
  const u = unitById(v, s.unitIds[s.i]);
  // Пример выбран при показе карточки и хранится в её состоянии: не меняется до оценки.
  const ex = s.card.example;
  if (!u || !ex?.audio) return `<div class="screen"><div class="sub">Запись не найдена.</div><button class="primary" data-act="skipCard" data-i="${s.i}">Дальше</button></div>`;
  const c = s.card;
  const top = sessionTop('Аудио', s.i, s.unitIds.length);
  if (!c.flipped) {
    return `<div class="screen">${top}
      <div class="center">
        <button class="bigplay" data-act="listenPlay" data-i="${s.i}" aria-label="Прослушать">${IC.play}</button>
        <div class="sub" aria-live="polite">Прослушано: ${c.plays}</div>
        <button class="chip ${c.slow ? 'on' : ''}" data-act="slow" aria-pressed="${c.slow}">Медленнее 0.75×</button>
        <div class="sub" style="max-width:260px">Текст скрыт. Попробуйте понять смысл на слух, можно слушать несколько раз.</div>
      </div>
      <button class="primary" data-act="flip">Показать ответ</button></div>`;
  }
  const reasons = (Object.keys(REASON_LABELS) as Reason[])
    .map((r) => `<button class="chip ${c.reasons.includes(r) ? 'on' : ''}" data-act="reason" data-r="${r}" aria-pressed="${c.reasons.includes(r)}" ${c.flag ? 'disabled' : ''}>${REASON_LABELS[r]}</button>`).join('');
  const flagChip = `<button class="chip flag ${c.flag ? 'on' : ''}" data-act="flagToggle" aria-pressed="${c.flag}">Ошибка в примере</button>`;
  const bottom = c.flag
    ? `<div class="card">
        <div class="sub">Карточка уйдёт из занятия без оценки, а этот пример перестанет звучать на аудиокарточке, пока его не исправят. Другие примеры фразы и текстовые повторения продолжатся.</div>
        <div class="sub">Что не так? (необязательно)</div>
        <div class="chips">${(Object.keys(PROBLEM_LABELS) as Exclude<Problem, ''>[]).map((p) => `<button class="chip ${c.problem === p ? 'on' : ''}" data-act="problem" data-p="${p}" aria-pressed="${c.problem === p}">${PROBLEM_LABELS[p]}</button>`).join('')}</div>
        <button class="primary" data-act="flagSubmit" data-i="${s.i}">Отложить до исправления</button>
      </div>`
    : `<div style="font-weight:600;text-align:center">Как вы поняли <u>до</u> того, как увидели текст?</div>
      <div class="rates">
        <button class="rate r1" data-act="listenRate" data-r="again" data-i="${s.i}">Не понял</button>
        <button class="rate r2" data-act="listenRate" data-r="hard" data-i="${s.i}">С трудом</button>
        <button class="rate r3" data-act="listenRate" data-r="good" data-i="${s.i}">Легко</button></div>`;
  return `<div class="screen">${top}
    <div class="card"><div class="en">${highlight(ex.en, ex.target)}</div><div class="ru">${esc(ex.ru)}</div>
      <div style="display:flex;gap:8px;align-items:center"><button class="icon-btn" data-act="listenPlay" data-i="${s.i}" aria-label="Прослушать ещё раз">${IC.spk}</button>
        <span class="sub">${esc(u.form)} — ${esc(u.meaning)}${c.replays ? ` · ещё прослушано: ${c.replays}` : ''}</span></div>
      <button class="more" data-act="openDetail" data-id="${esc(u.id)}">Подробнее о фразе →</button></div>
    <div class="sub">Что помешало? (необязательно)</div><div class="chips">${reasons}</div>
    <div class="chips">${flagChip}</div>
    <div style="flex:1"></div>
    ${bottom}
  </div>`;
}

export function doneView(s: Extract<Session, { kind: 'done' }>): string {
  const msg = s.first
    ? `Вы познакомились с ${s.count} ${plural(s.count, ['новой фразой', 'новыми фразами', 'новыми фразами'])} и прошли первую проверку. Следующая проверка этих фраз будет позже по расписанию.`
    : 'Занятие завершено. Следующие повторения появятся по расписанию.';
  return `<div class="screen"><div class="center">
    <h1>Готово</h1>
    <div class="sub" style="max-width:280px">${msg}</div>
    <button class="primary" style="max-width:280px" data-act="finish">На главный экран</button>
  </div></div>`;
}

// ——— Настройки и прогресс ———

const ORDERS: [PoolOrder, string][] = [['own_first', 'Сначала свои'], ['sets_first', 'Сначала из наборов'], ['random', 'Случайно']];

export function settingsView(v: View): string {
  const f = v.settings;
  const d = f.draft ?? v.snap.settings;
  const e = f.errors;
  return `<div class="screen">
    <div class="top"><button class="icon-btn" data-go="today" aria-label="Назад">${IC.back}</button><div class="title">Настройки</div><div style="width:44px"></div></div>
    <form class="card" data-form="settings" novalidate>
      <label class="field">Новых фраз за занятие (0–20)
        <input type="number" name="newPerSession" min="0" max="20" step="1" inputmode="numeric" value="${esc(String(d.newPerSession))}"${e.newPerSession ? ' aria-invalid="true"' : ''}>
        ${fieldErr(e, 'newPerSession')}</label>
      <fieldset class="field"><legend>Порядок выбора из пула</legend>
        <div class="chips">${ORDERS.map(([k, l]) => `<label class="chip ${d.poolOrder === k ? 'on' : ''}"><input type="radio" name="poolOrder" value="${k}" ${d.poolOrder === k ? 'checked' : ''}>${l}</label>`).join('')}</div>
        ${fieldErr(e, 'poolOrder')}</fieldset>
      ${pushSection(v, d, e)}
      ${f.message ? `<div class="${f.message.startsWith('Сохранено') ? 'sub' : 'err'}" role="status">${esc(f.message)}</div>` : ''}
      ${v.online ? '' : `<div class="warn">Нет сети. Настройки можно менять только при подключении к серверу.</div>`}
      <button class="primary" type="submit" ${f.busy || !v.online ? 'disabled' : ''}>${f.busy ? 'Сохраняем…' : 'Сохранить'}</button>
    </form>
    <div class="card">
      <div class="kv"><span>Аккаунт</span><span class="sub">${esc(v.snap.email)}</span></div>
      ${syncLine(v)}
      <button class="secondary" data-act="logout" ${v.online ? '' : 'disabled'}>Выйти</button>
    </div>
  </div>`;
}

/** Уведомления о текстовых повторениях: переключатель для этого устройства, тихие часы, лимит, пробное. */
function pushSection(v: View, d: Settings, e: Record<string, string>): string {
  const p = v.push;
  const dis = !p.canToggle || p.busy ? ' disabled' : '';
  const label = p.busy ? 'Подождите…' : p.on ? 'Включены ✓' : 'Включить';
  const time = (name: 'quietStart' | 'quietEnd', label: string, val: string) =>
    `<label class="field grow">${label}<input type="time" name="${name}" value="${esc(val)}" step="60"${e[name] ? ' aria-invalid="true"' : ''}>${fieldErr(e, name)}</label>`;
  return `<fieldset class="field"><legend>Уведомления</legend>
      <div class="item"><div class="item-txt"><div style="font-weight:500">О текстовых повторениях</div>
        <div class="sub">На этом устройстве. Приходят пакетом, когда накопились карточки.</div></div>
        <button type="button" class="chip ${p.on ? 'on' : ''}" data-act="pushToggle" aria-pressed="${p.on}"${dis}>${label}</button></div>
      ${p.note ? `<div class="warn">${esc(p.note)}</div>` : ''}
      ${p.message ? `<div class="${p.message.startsWith('Отправлено') ? 'sub' : 'err'}" role="status">${esc(p.message)}</div>` : ''}
      <div class="sub" style="margin-top:10px">Тихие часы — уведомления не приходят</div>
      <div style="display:flex;gap:8px">${time('quietStart', 'С', d.quietStart ?? '22:00')}${time('quietEnd', 'До', d.quietEnd ?? '09:00')}</div>
      <label class="field">Не больше уведомлений в день (1–10)
        <input type="number" name="maxPushPerDay" min="1" max="10" step="1" inputmode="numeric" value="${esc(String(d.maxPushPerDay ?? 4))}"${e.maxPushPerDay ? ' aria-invalid="true"' : ''}>
        ${fieldErr(e, 'maxPushPerDay')}</label>
      ${p.on ? `<button type="button" class="secondary" data-act="pushTest"${p.testBusy || !v.online ? ' disabled' : ''}>${p.testBusy ? 'Отправляем…' : 'Отправить пробное'}</button>` : ''}
    </fieldset>`;
}

export function progressView(): string {
  return `<div class="screen">
    <h1>Прогресс</h1>
    <div class="card"><div class="sub">Статистика по тексту и по слуху появится в одном из следующих обновлений.</div></div>
  </div>${nav('progress')}`;
}
